/**
 * ToolPreviewPopup - Ventana flotante con información detallada de cada herramienta
 */
import { createPortal } from 'react-dom';
import { 
  ChartLine, TrendUp, Lightning, Calculator, ArrowsClockwise, MagnifyingGlass,
  CalendarBlank, Scales, ChartPieSlice, Database, Buildings, CurrencyCircleDollar,
  Briefcase, Newspaper, Bell, Monitor
} from '@phosphor-icons/react';

// Información extendida de cada herramienta con acciones clickeables
const TOOL_DETAILS = {
  rendimiento: {
    title: 'Análisis de Rendimiento',
    subtitle: 'Métricas de performance',
    features: [
      { icon: '📅', text: 'Cambios: 1D, 1S, 1M, 3M, 6M, 1A', action: 'open' },
      { icon: '🎯', text: 'Sentimiento del mercado', action: 'open' },
      { icon: '⭐', text: 'Recomendación de analistas', action: 'open' },
      { icon: '📊', text: 'Comparativa con índices', action: 'open' },
    ],
    tip: 'Evalúa el rendimiento histórico antes de invertir'
  },
  signals: {
    title: 'Análisis Señales Trading',
    subtitle: 'Alertas automáticas',
    features: [
      { icon: '🕯️', text: 'Patrones de velas japonesas', action: 'open' },
      { icon: '🟢', text: 'Señales de COMPRA automáticas', action: 'open' },
      { icon: '🔴', text: 'Señales de VENTA automáticas', action: 'open' },
      { icon: '⚡', text: 'Detección de breakouts', action: 'open' },
    ],
    tip: 'Las señales son orientativas, siempre haz tu propio análisis'
  },
  calculators: {
    title: 'Calculadoras de Trading',
    subtitle: 'Gestión de riesgo',
    features: [
      { icon: '📐', text: 'Tamaño de posición óptimo', action: 'positionSize' },
      { icon: '🛑', text: 'Calculador de Stop Loss', action: 'stopLoss' },
      { icon: '🎯', text: 'Calculador de Take Profit', action: 'takeProfit' },
      { icon: '⚖️', text: 'Ratio Riesgo/Beneficio', action: 'riskReward' },
    ],
    tip: 'Nunca arriesgues más del 1-2% de tu capital por operación'
  },
  comparator: {
    title: 'Comparador de Activos',
    subtitle: 'Análisis lado a lado',
    features: [
      { icon: '🔄', text: 'Compara hasta 4 activos', action: 'open' },
      { icon: '📈', text: 'Gráficos superpuestos', action: 'open' },
      { icon: '📊', text: 'Correlación de precios', action: 'open' },
      { icon: '💹', text: 'Rendimiento relativo', action: 'open' },
    ],
    tip: 'Ideal para encontrar activos descorrelacionados'
  },
  analyzer: {
    title: 'Analizador de Activos',
    subtitle: 'Análisis profundo',
    features: [
      { icon: '🎛️', text: 'Velocímetro de posición anual', action: 'open' },
      { icon: '📜', text: 'Historial de análisis guardados', action: 'open' },
      { icon: '📄', text: 'Exportar a PDF profesional', action: 'exportPdf' },
      { icon: '📊', text: 'Estadísticas de volatilidad', action: 'open' },
    ],
    tip: 'Guarda tus análisis para revisarlos después'
  },
  economic: {
    title: 'Calendario Económico',
    subtitle: 'Eventos macroeconómicos',
    features: [
      { icon: '🏛️', text: 'Decisiones de bancos centrales', action: 'open' },
      { icon: '📰', text: 'Datos de empleo (NFP)', action: 'open' },
      { icon: '💰', text: 'Inflación (CPI, PPI)', action: 'open' },
      { icon: '🏭', text: 'PMI e índices de producción', action: 'open' },
    ],
    tip: 'Los eventos económicos pueden mover el mercado bruscamente'
  },
  indices: {
    title: 'Comparar con Índices',
    subtitle: 'Benchmark de mercado',
    features: [
      { icon: '🇺🇸', text: 'S&P 500, NASDAQ, Dow Jones', action: 'addSPY' },
      { icon: '🇪🇸', text: 'IBEX 35', action: 'addIBEX' },
      { icon: '🇩🇪', text: 'DAX 40', action: 'addDAX' },
      { icon: '🌍', text: 'MSCI World, Emerging Markets', action: 'addMSCI' },
    ],
    tip: 'Compara tu activo con el mercado general'
  },
  categories: {
    title: 'Análisis de Sectores',
    subtitle: 'Análisis sectorial',
    features: [
      { icon: '💻', text: 'Tecnología' },
      { icon: '🏦', text: 'Finanzas' },
      { icon: '⚕️', text: 'Salud' },
      { icon: '⚡', text: 'Energía' },
      { icon: '🏭', text: 'Industrial' },
    ],
    tip: 'La rotación sectorial puede indicar cambios de tendencia'
  },
  correlations: {
    title: 'Matriz de Correlaciones',
    subtitle: 'Diversificación inteligente',
    features: [
      { icon: '🔗', text: 'Correlación entre activos' },
      { icon: '🎨', text: 'Mapa de calor visual' },
      { icon: '📊', text: 'Coeficientes de Pearson' },
      { icon: '⚖️', text: 'Optimización de cartera' },
    ],
    tip: 'Activos con baja correlación reducen el riesgo'
  },
  backtesting: {
    title: 'Backtesting',
    subtitle: 'Prueba tu estrategia',
    features: [
      { icon: '⏪', text: 'Simula estrategias históricas' },
      { icon: '📈', text: 'Métricas de rendimiento' },
      { icon: '📉', text: 'Drawdown máximo' },
      { icon: '🎯', text: 'Ratio de aciertos' },
    ],
    tip: 'El rendimiento pasado no garantiza resultados futuros'
  },
  sectors: {
    title: 'Análisis de Sectores',
    subtitle: 'Rendimiento sectorial',
    features: [
      { icon: '📊', text: 'Performance por sector' },
      { icon: '🔄', text: 'Rotación sectorial' },
      { icon: '📈', text: 'Líderes y rezagados' },
      { icon: '💹', text: 'Tendencias sectoriales' },
    ],
    tip: 'Identifica sectores en tendencia alcista'
  },
  dividends: {
    title: 'Calendario de Dividendos',
    subtitle: 'Ingresos pasivos',
    features: [
      { icon: '💵', text: 'Próximas fechas de pago' },
      { icon: '📅', text: 'Fecha ex-dividendo' },
      { icon: '📊', text: 'Historial de pagos' },
      { icon: '💰', text: 'Dividend Yield anual' },
    ],
    tip: 'Compra antes de la fecha ex-dividendo para recibir el pago'
  },
  portfolio: {
    title: 'Mi Portafolio',
    subtitle: 'Gestión de inversiones',
    features: [
      { icon: '💼', text: 'Tus posiciones actuales' },
      { icon: '📈', text: 'Beneficio/Pérdida total' },
      { icon: '📊', text: 'Distribución por activo' },
      { icon: '📄', text: 'Exportar a PDF' },
    ],
    tip: 'Gestiona tus inversiones y exporta reportes'
  },
  livePortfolio: {
    title: 'Cartera en Vivo',
    subtitle: 'Gráfico en tiempo real',
    features: [
      { icon: '📈', text: 'Gráfico de evolución en vivo' },
      { icon: '💹', text: 'Valor total actualizado cada 30s' },
      { icon: '📊', text: 'Cambio porcentual del día' },
      { icon: '🔔', text: 'Alertas de cambios bruscos' },
    ],
    tip: 'Sigue la evolución de tu cartera en tiempo real'
  },
  contabilidad: {
    title: 'Contabilidad',
    subtitle: 'Registro de beneficios (Ultimate)',
    features: [
      { icon: '📊', text: 'Registro diario de B/P' },
      { icon: '📅', text: 'Cálculo preciso de períodos' },
      { icon: '🏆', text: 'Mejor/peor día, rachas' },
      { icon: '📈', text: 'Gráfico de evolución' },
    ],
    tip: 'Guarda al cierre del mercado para datos precisos'
  },
  news: {
    title: 'Noticias del Mercado',
    subtitle: 'Información en tiempo real',
    features: [
      { icon: '📰', text: 'Noticias del activo seleccionado' },
      { icon: '🌍', text: 'Traducción automática al español' },
      { icon: '🔔', text: 'Contador de no leídas' },
      { icon: '📱', text: 'Actualizaciones en tiempo real' },
    ],
    tip: 'Las noticias pueden mover los precios rápidamente'
  },
  alerts: {
    title: 'Alertas de Precio',
    subtitle: 'Notificaciones personalizadas',
    features: [
      { icon: '🔔', text: 'Alertas cuando sube/baja' },
      { icon: '🔊', text: 'Sonido de notificación' },
      { icon: '🎯', text: 'Precio objetivo personalizado' },
      { icon: '⏰', text: 'Alertas múltiples por activo' },
    ],
    tip: 'Configura alertas para no perder oportunidades'
  },
  fullscreen: {
    title: 'Modo Pantalla Grande',
    subtitle: 'Vista de presentación',
    features: [
      { icon: '🖥️', text: 'Ideal para monitores grandes' },
      { icon: '📊', text: 'Más espacio para gráficos' },
      { icon: '👁️', text: 'Vista limpia y profesional' },
      { icon: '📈', text: 'Perfecto para trading rooms' },
    ],
    tip: 'Usa ESC para salir del modo pantalla completa'
  },
  indicadoresPro: {
    title: 'Análisis Técnico Profesional',
    subtitle: 'Panel completo de indicadores',
    features: [
      { icon: '📈', text: 'RSI, MACD, Bollinger, ADX' },
      { icon: '🎯', text: 'Fibonacci y ATR' },
      { icon: '📊', text: 'Señales combinadas' },
      { icon: '💡', text: 'Explicaciones para principiantes' },
    ],
    tip: 'El panel más completo de análisis técnico'
  },
  candlestickChart: {
    title: 'Mapa Velas Ampliado',
    subtitle: 'Gráfico de velas profesional',
    features: [
      { icon: '🕯️', text: 'Gráfico de velas interactivo' },
      { icon: '🔍', text: 'Zoom y navegación temporal' },
      { icon: '🖨️', text: 'Imprimir gráfico' },
      { icon: '📐', text: 'Redimensionable' },
    ],
    tip: 'Analiza patrones de velas con detalle'
  },
  fundamentalAnalysis: {
    title: 'Análisis con IA',
    subtitle: 'Análisis con Inteligencia Artificial',
    features: [
      { icon: '🤖', text: 'Análisis generado por IA' },
      { icon: '📊', text: 'Score de inversión 0-100' },
      { icon: '💰', text: 'Métricas fundamentales' },
      { icon: '✅', text: 'Recomendación automática' },
    ],
    tip: 'Obtén un análisis profesional en segundos'
  },
  indexCryptoComparator: {
    title: 'Comparador Índices y Cripto',
    subtitle: 'Índices mundiales y criptomonedas',
    features: [
      { icon: '🌍', text: 'Índices globales: S&P, DAX, IBEX' },
      { icon: '₿', text: 'Principales criptomonedas' },
      { icon: '📈', text: 'Comparación de rendimiento' },
      { icon: '💡', text: 'Info para principiantes' },
    ],
    tip: 'Compara mercados y criptomonedas fácilmente'
  },
  investmentSimulator: {
    title: 'Simulador de Inversiones',
    subtitle: 'El poder del interés compuesto',
    features: [
      { icon: '📈', text: 'Simulación de interés compuesto' },
      { icon: '💰', text: 'Presets: Conservador, Agresivo, Jubilación' },
      { icon: '📊', text: 'Gráfico de evolución a largo plazo' },
      { icon: '🏦', text: 'Cálculo de ingresos pasivos' },
      { icon: '💡', text: 'Tooltips educativos para principiantes' },
    ],
    tip: 'Descubre cómo pequeñas aportaciones crecen con el tiempo'
  },
  financialGoals: {
    title: 'Objetivos Financieros',
    subtitle: 'Define y alcanza tus metas',
    features: [
      { icon: '🎯', text: 'Crea múltiples objetivos de ahorro' },
      { icon: '🏠', text: 'Tipos: Casa, Vacaciones, Jubilación, Emergencias' },
      { icon: '📊', text: 'Gráficos de progreso visual' },
      { icon: '💰', text: 'Añade aportaciones rápidas (+€ Enter)' },
      { icon: '🖨️', text: 'Imprime tu plan financiero' },
    ],
    tip: 'Visualiza tu progreso hacia la libertad financiera'
  },
  heatmapCartera: {
    title: 'Análisis Heatmap',
    subtitle: 'Mapa de calor de cartera y mercado',
    features: [
      { icon: '🔥', text: 'Heatmap visual de tu cartera' },
      { icon: '🌍', text: 'Análisis del mercado por sectores' },
      { icon: '📊', text: 'Tamaño proporcional al valor' },
      { icon: '🎨', text: 'Colores por rendimiento HOY/Total' },
      { icon: '🔍', text: 'Filtros y ordenación avanzada' },
    ],
    tip: 'Visualiza de un vistazo qué activos y sectores dominan tu cartera'
  },
  ebitdaAnalysis: {
    title: 'Análisis Margen EBITDA',
    subtitle: 'Métricas fundamentales avanzadas',
    features: [
      { icon: '📊', text: 'Margen EBITDA por trimestre' },
      { icon: '💰', text: 'ROE, ROA, ROI' },
      { icon: '📈', text: 'P/E, P/B, PEG Ratio' },
      { icon: '💳', text: 'Deuda/EBITDA y apalancamiento' },
      { icon: '📋', text: 'Análisis de cartera completa' },
    ],
    tip: 'Evalúa la salud financiera real de las empresas antes de invertir'
  },
  finvizMap: {
    title: 'Mapa Finviz',
    subtitle: 'Treemap visual del mercado',
    features: [
      { icon: '🗺️', text: 'Mapa de calor tipo Finviz' },
      { icon: '🇺🇸', text: 'S&P 500 completo' },
      { icon: '🇪🇺', text: 'Europa y IBEX 35' },
      { icon: '🏭', text: 'Filtros por sector' },
      { icon: '🔍', text: 'Click para ver detalles' },
    ],
    tip: 'Visualiza todo el mercado de un vistazo para detectar oportunidades'
  },
  multiChart: {
    title: 'Multi-Gráfico',
    subtitle: '4 activos simultáneos',
    features: [
      { icon: '📊', text: 'Analiza 4 activos al mismo tiempo' },
      { icon: '🔄', text: 'Gráficos independientes' },
      { icon: '📅', text: 'Períodos ajustables por gráfico' },
      { icon: '🖥️', text: 'Vista en pantalla completa' },
    ],
    tip: 'Ideal para comparar activos de un mismo sector o vigilar varios mercados'
  },
  heatmap: {
    title: 'Análisis de Correlación',
    subtitle: 'Mapa de calor de correlaciones',
    features: [
      { icon: '🗺️', text: 'Matriz de correlaciones visual' },
      { icon: '📊', text: 'Tips de diversificación' },
      { icon: '📅', text: 'Períodos ajustables' },
      { icon: '💡', text: 'Detecta activos correlacionados' },
    ],
    tip: 'Diversifica mejor tu cartera identificando qué activos se mueven juntos'
  },
  extremes: {
    title: 'Historial Máx/Mín',
    subtitle: 'Máximos y mínimos históricos',
    features: [
      { icon: '📈', text: 'ATH (All-Time High)' },
      { icon: '📉', text: 'ATL (All-Time Low)' },
      { icon: '📅', text: 'Máx/Mín 52 semanas' },
      { icon: '📊', text: 'Gráfico con marcadores' },
    ],
    tip: 'Saber si un activo está cerca de máximos o mínimos ayuda a decidir'
  },
  economicCalendar: {
    title: 'Calendario Económico',
    subtitle: 'Eventos que mueven el mercado',
    features: [
      { icon: '🏛️', text: 'Decisiones FED, BCE' },
      { icon: '📰', text: 'Resultados empresariales' },
      { icon: '⚠️', text: 'Impacto esperado (Alto/Medio/Bajo)' },
      { icon: '💡', text: 'Explicaciones para principiantes' },
    ],
    tip: 'Los eventos económicos pueden provocar movimientos bruscos'
  },
  dataArchive: {
    title: 'Archivo de Datos',
    subtitle: 'Historial y evolución',
    features: [
      { icon: '📁', text: 'Registro diario automático' },
      { icon: '📊', text: 'Gráficos por día/mes/año' },
      { icon: '📤', text: 'Exportar datos' },
      { icon: '🖨️', text: 'Imprimir informes' },
    ],
    tip: 'Se guarda automáticamente a las 22:00 cada día'
  },
  buystrategy: {
    title: 'Estrategia de Compra',
    subtitle: 'DCA, rebalanceo y objetivos',
    features: [
      { icon: '💡', text: 'Calculadora DCA automático' },
      { icon: '🎯', text: 'Precio objetivo' },
      { icon: '⚖️', text: 'Rebalanceo de cartera' },
      { icon: '📊', text: 'Valoración de activos' },
    ],
    tip: 'Planifica tus compras para reducir el impacto de la volatilidad'
  },
  notifications: {
    title: 'Notificaciones',
    subtitle: 'Push y Email',
    features: [
      { icon: '📱', text: 'Notificaciones push del navegador' },
      { icon: '📧', text: 'Alertas por email' },
      { icon: '⚙️', text: 'Configuración personalizada' },
      { icon: '📜', text: 'Historial de notificaciones' },
    ],
    tip: 'Activa las notificaciones para no perderte movimientos importantes'
  },
  backup: {
    title: 'Backup',
    subtitle: 'Copias de seguridad',
    features: [
      { icon: '💾', text: 'Gestiona backups de tus inversiones' },
      { icon: '🔄', text: 'Restaurar datos' },
      { icon: '🛡️', text: 'Protección automática' },
      { icon: '📤', text: 'Exportar/Importar JSON' },
    ],
    tip: 'Haz backups periódicos para no perder nunca tu información'
  },
  manual: {
    title: 'Manual',
    subtitle: 'Guía de usuario',
    features: [
      { icon: '📖', text: 'Tutorial paso a paso' },
      { icon: '🎓', text: 'Cómo usar cada herramienta' },
      { icon: '💡', text: 'Tips y trucos' },
      { icon: '❓', text: 'Preguntas frecuentes' },
    ],
    tip: 'Consulta el manual si tienes dudas sobre alguna función'
  },
  stockScreener: {
    title: 'Buscador Acciones',
    subtitle: 'Filtros avanzados tipo Investing',
    features: [
      { icon: '🔍', text: '400+ tickers disponibles' },
      { icon: '🌍', text: 'USA, Europa, Emergentes' },
      { icon: '⚙️', text: 'Filtros: P/E, Dividendos, Sector' },
      { icon: '📊', text: 'Similar a Investing.com' },
    ],
    tip: 'Encuentra acciones que cumplan tus criterios de inversión'
  },
  stockComparator: {
    title: 'Comparador Acciones',
    subtitle: 'Compara métricas lado a lado',
    features: [
      { icon: '⚖️', text: 'Compara 2-4 acciones' },
      { icon: '📊', text: 'Métricas fundamentales' },
      { icon: '📈', text: 'Gráfico comparativo' },
      { icon: '✅', text: 'Recomendación automática' },
    ],
    tip: 'Compara antes de decidir en qué empresa invertir'
  },
  financialDatabase: {
    title: 'Datos Financieros',
    subtitle: 'Estados y ratios',
    features: [
      { icon: '📊', text: 'Estados Financieros completos' },
      { icon: '📈', text: 'Ratios de Valoración (PER, PEG)' },
      { icon: '💰', text: 'Salud Financiera (ROE, Deuda)' },
      { icon: '💵', text: 'Historial de Dividendos' },
    ],
    tip: 'Analiza los fundamentales antes de invertir a largo plazo'
  },
  marketDatabase: {
    title: 'Base de Datos',
    subtitle: 'Tabla Excel de mercado',
    features: [
      { icon: '📊', text: 'Top 20 activos del mercado' },
      { icon: '📈', text: 'Máx/Mín por período' },
      { icon: '⬆️', text: 'Mayor subida/bajada' },
      { icon: '⭐', text: 'Recomendación de expertos' },
    ],
    tip: 'Explora el mercado como en una hoja de cálculo profesional'
  },
  historicalData: {
    title: 'Datos Históricos',
    subtitle: 'Historial de cotizaciones',
    features: [
      { icon: '📅', text: 'Historial OHLCV completo' },
      { icon: '📤', text: 'Exportar CSV/PDF' },
      { icon: '🖨️', text: 'Imprimir datos' },
      { icon: '⚙️', text: 'Diario/Semanal/Mensual' },
    ],
    tip: 'Descarga datos históricos para tu propio análisis'
  },
  dividendTracker: {
    title: 'Gestor de Dividendos',
    subtitle: 'Ingresos pasivos de tu cartera',
    features: [
      { icon: '💰', text: 'Dividend Yield de cada activo' },
      { icon: '📅', text: 'Fecha ex-dividendo' },
      { icon: '📊', text: 'Ingresos anuales estimados' },
      { icon: '📈', text: 'Payout Ratio' },
    ],
    tip: 'Construye una cartera de dividendos para ingresos pasivos'
  },
};

const ToolPreviewPopup = ({ toolId, isVisible, position, sectionColor, fallback, onSelect, onFeatureSelect, onMouseEnter, onMouseLeave }) => {
  if (!isVisible || !toolId) return null;

  // Identidad de marca: el popup usa el acento dorado (no el color por herramienta)
  sectionColor = '#E8C55C';

  // Detalle rico si existe; si no, se construye uno a partir de la info de la sección
  // (label + description + preview) para que TODAS las herramientas muestren su ventana.
  let details = TOOL_DETAILS[toolId];
  if (!details && fallback) {
    const bullets = (fallback.preview || '')
      .split('•')
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 5);
    details = {
      title: fallback.label || 'Herramienta',
      subtitle: fallback.description || '',
      features: bullets.length
        ? bullets.map((text) => ({ icon: '▸', text }))
        : [{ icon: '▸', text: fallback.description || 'Abrir esta herramienta' }],
      tip: 'Haz clic para abrir esta herramienta',
    };
  }
  if (!details) return null;
  
  const handleClick = (e) => {
    // Solo abrir si click en el fondo, no en features
    if (e.target === e.currentTarget || e.target.closest('.feature-button') === null) {
      if (onSelect) {
        onSelect(toolId);
      }
    }
  };
  
  const handleFeatureClick = (e, feature, idx) => {
    e.stopPropagation();
    
    // Si la feature tiene un 'special' que indica abrir otra ventana
    if (feature.special && onSelect) {
      onSelect(feature.special);
      return;
    }
    
    if (onFeatureSelect) {
      onFeatureSelect(toolId, feature, idx);
    } else if (onSelect) {
      // Si no hay handler específico, abrir la herramienta
      onSelect(toolId);
    }
  };
  
  const content = (
    <div 
      className="fixed z-[9999] animate-in fade-in zoom-in-95 duration-200"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translateY(-50%)'
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div 
        className="rounded-xl p-4 min-w-[300px] max-w-[350px] shadow-2xl transition-all"
        style={{
          background: 'linear-gradient(135deg, rgba(10, 25, 45, 0.98) 0%, rgba(5, 15, 30, 0.98) 100%)',
          border: `1px solid ${sectionColor}50`,
          boxShadow: `0 20px 50px rgba(0,0,0,0.6), 0 0 40px ${sectionColor}15, inset 0 1px 0 rgba(255,255,255,0.05)`
        }}
      >
        {/* Header - clickeable para abrir herramienta */}
        <div 
          className="mb-3 pb-3 border-b border-gray-700/50 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={handleClick}
        >
          <h3 className="text-base font-bold" style={{ color: sectionColor }}>
            {details.title}
          </h3>
          <p className="text-xs text-gray-400">{details.subtitle}</p>
        </div>
        
        {/* Features - solo los que tienen 'special' son clickeables */}
        <div className="space-y-1 mb-3">
          {details.features.map((feature, idx) => (
            feature.special ? (
              // Botón clickeable que abre otra ventana
              <button
                key={`feature-${feature.text}-${idx}`}
                onClick={(e) => handleFeatureClick(e, feature, idx)}
                className="feature-button w-full flex items-center gap-2 text-sm p-2 rounded-lg transition-all hover:bg-white/10 cursor-pointer text-left group border border-transparent hover:border-gold-500/30"
              >
                <span className="text-base group-hover:scale-110 transition-transform">{feature.icon}</span>
                <span className="text-gray-300 group-hover:text-white transition-colors flex-1">{feature.text}</span>
                <span 
                  className="text-[10px] px-2 py-0.5 rounded"
                  style={{ backgroundColor: `${sectionColor}30`, color: sectionColor }}
                >
                  Abrir
                </span>
              </button>
            ) : (
              // Texto informativo (no clickeable)
              <div
                key={`info-${feature.text}-${idx}`}
                className="flex items-center gap-2 text-sm p-2 rounded-lg"
              >
                <span className="text-base">{feature.icon}</span>
                <span className="text-gray-400 flex-1">{feature.text}</span>
              </div>
            )
          ))}
        </div>
        
        {/* Tip */}
        <div 
          className="mt-3 pt-3 border-t border-gray-700/50 flex items-start gap-2"
        >
          <span className="text-yellow-500">💡</span>
          <p className="text-[11px] text-gray-400 italic leading-relaxed">
            {details.tip}
          </p>
        </div>
        
        {/* Footer hint */}
        <div className="mt-3 text-center">
          <button
            onClick={handleClick}
            className="text-xs px-4 py-1.5 rounded-full transition-all hover:scale-105"
            style={{ 
              backgroundColor: `${sectionColor}25`,
              color: sectionColor,
              border: `1px solid ${sectionColor}40`
            }}
          >
            Abrir {details.title}
          </button>
        </div>
      </div>
    </div>
  );
  
  return createPortal(content, document.body);
};

export default ToolPreviewPopup;
